import React, {Dispatch} from "react";
import {produce} from "immer";
import {Actions, ActionType, State, VisibleLegsAction, VisibleLegsReducer} from "../state";
import {AddPrefixToKeys} from "../../../../../common/util/ts-helpers";
import * as Util from "../util";
import {difference, union} from "lodash";
import {Phase2ScheduleNode} from "../types";
import {getSNodeID} from "../../../../../components/scheduling/util";
import {ScheduleNode} from "../../../../../components/scheduling/types";
import {usePurgeFromFlight} from "./purge-from-flight";
import {Phase2Api} from "../api";
import {message} from "antd";
import {isFlightScheduled} from "../util";

export const DefaultState = {
    modal: {
        open: false
    },
    data: {
        entities: [] as Phase2ScheduleNode[]
    },
    selection: {
        selectedEntityIds: [] as string[]
    },
    editor: {
        visibleLegs: [] as { departureID: string, destinationID: string }[]
    },
    purgeStatus: {
        flightIsUpdating: false,
        error: null as Error
    }
}

export type PurgeToolState = typeof DefaultState;

export type PurgeToolAction = {
    type: 'PURGE_TOOL.SET_VISIBLE',
    payload: {
        open: boolean
    }
} | {
    type: 'PURGE_TOOL.SET_SELECTED_ENTITY_IDS',
    add?: typeof DefaultState.selection.selectedEntityIds,
    remove?: typeof DefaultState.selection.selectedEntityIds,
    set?: typeof DefaultState.selection.selectedEntityIds
} | AddPrefixToKeys<VisibleLegsAction, 'PURGE_TOOL.', 'type'> |
{
    type: 'PURGE_TOOL.SET_PURGE_STATUS',
    payload: {
        status: 'LOADING' | 'SUCCESS'
    } | {
        status: 'ERROR',
        error: Error
    }
}

export const useApi = (api: Phase2Api) => {

    const state = api.getState();
    const dispatch = api.getDispatch();
    const purgeToolState = state.purgeEntitiesFromFlightTool;

    const startPurge = usePurgeFromFlight(api);

    const purgeToolApi = {
        modal: {
            isOpen: () => purgeToolState.modal.open,
            setOpen: (open: boolean) => dispatch({
                type: 'PURGE_TOOL.SET_VISIBLE',
                payload: { open: open }
            })
        },
        startPurge: () => {
            startPurge()
                .then(() => {
                    message.success('Pax/Cgo successfully purged from flight(s)');
                    purgeToolApi.modal.setOpen(false);
                    api.data.refetch();
                })
                .catch(() => message.error('Failed to purge pax/cgo from flight. View console for details.'))
        },
        isPurging: () => purgeToolState.purgeStatus.flightIsUpdating,
        selectedEntityIds: state.purgeEntitiesFromFlightTool.selection.selectedEntityIds as
            Readonly<typeof state.purgeEntitiesFromFlightTool.selection.selectedEntityIds>
    }

    return purgeToolApi;
}

export type Api = ReturnType<typeof useApi>

export const Reducer: React.Reducer<State, Actions>
    = (state, action) => produce(state, (draft) => {

    const purgeToolState = draft.purgeEntitiesFromFlightTool;

    function initData(){
        const entitiesOnValidFlightOnly = state.data.originalEntities
            .filter((entity) => isFlightScheduled(entity.currentCarrierID));

        purgeToolState.data.entities = entitiesOnValidFlightOnly;

        purgeToolState.selection.selectedEntityIds = [];

        const data = Util.DepDestList.build(entitiesOnValidFlightOnly);

        const depDestList = data
            .map(item => ({
                departureID: item.departure._id,
                destinationID: item.destination._id
            }));

        purgeToolState.editor.visibleLegs = depDestList;
    }

    switch (action.type){
        case ActionType.INIT_DATA:
            if (!state.purgeEntitiesFromFlightTool.modal.open) return;
            initData();
            break;
        case ActionType.RESET_DATA:
            if (!state.purgeEntitiesFromFlightTool.modal.open) return;
            initData();
            break;
        case "PURGE_TOOL.SET_VISIBLE":
            if (action.payload.open == purgeToolState.modal.open) return;
            purgeToolState.modal.open = action.payload.open;
            initData();

            // Select all entities upon opening
            purgeToolState.selection.selectedEntityIds = purgeToolState
                .data.entities.map(entity => getSNodeID(entity as ScheduleNode));
            break;
        case "PURGE_TOOL.SET_VISIBLE_LEGS":
            purgeToolState.editor.visibleLegs = VisibleLegsReducer(purgeToolState.editor.visibleLegs, {
                ...action,
                type: ActionType.SET_VISIBLE_LEGS
            })
            break;
        case "PURGE_TOOL.SET_SELECTED_ENTITY_IDS":
            if (!action.add && !action.remove && !action.set) return;

            let newSelEntityIds = purgeToolState.selection.selectedEntityIds;

            if (action.set){
                newSelEntityIds = action.set;
            }
            if (action.remove){
                newSelEntityIds = difference(newSelEntityIds, action.remove);
            }
            if (action.add){
                newSelEntityIds = union(newSelEntityIds, action.add);
            }

            purgeToolState.selection.selectedEntityIds = newSelEntityIds;
            break;
        case "PURGE_TOOL.SET_PURGE_STATUS":
            const { purgeStatus } = draft.purgeEntitiesFromFlightTool;

            switch (action.payload.status){
                case 'SUCCESS':
                    purgeStatus.flightIsUpdating = false;
                    purgeStatus.error = null;
                    break;
                case 'LOADING':
                    purgeStatus.flightIsUpdating = true;
                    purgeStatus.error = null;
                    break;
                case 'ERROR':
                    purgeStatus.flightIsUpdating = false;
                    purgeStatus.error = action.payload.error;
            }
            break;
    }
})